import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Award, FileText, Globe, BarChart, Target, Clipboard, Heart } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { resourceCategories } from '@/mockData';

const iconMap = {
  BookOpen,
  Award,
  FileText,
  Globe,
  BarChart,
  Target,
  Clipboard,
  Heart
};

// Per-category CTA + route overrides.
// Modules that are live (IELTS, GMAT) link to the AI learning system directly.
// Pre-launch modules show a "Coming Soon" badge and fall back to the resource library.
const CATEGORY_OVERRIDES = {
  ielts:          { cta: 'Start IELTS Prep →',         to: '/ielts',                  liveBadge: 'AI Live' },
  gmat:           { cta: 'Start GMAT Prep →',          to: '/gmat',                   liveBadge: 'AI Live' },
  vce:            { cta: 'View VCE Resources →',       to: '/resources/vce' },
  selective:      { cta: 'View Selective Resources →', to: '/resources/selective-entry' },
  eal:            { cta: 'View EAL Resources →',       to: '/resources/eal' },
  gre:            { cta: 'Coming Soon',                to: '/resources/gre',          comingSoon: true },
  sat:            { cta: 'Coming Soon',                to: '/resources/sat',          comingSoon: true },
  'special-needs':{ cta: 'Request Trial →',            to: '/contact?topic=special-needs' },
};

const ResourcesHub = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCategoryClick = (categoryId) => {
    const override = CATEGORY_OVERRIDES[categoryId];
    if (override?.to) {
      if (override.to.startsWith('http')) window.location.assign(override.to);
      else navigate(override.to);
      return;
    }
    navigate(`/resources/${categoryId}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Study Resources | Winhouse Tutors"
        description="Free and premium study resources for VCE, IELTS, Selective Entry, SAT, GMAT, GRE and more."
        path="/resources"
      />
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-stone-50 border-b" style={{borderBottomColor: 'rgba(183, 191, 114, 0.20)', paddingTop: '6rem'}}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-block mb-6">
            <div className="h-1 w-20 mx-auto" style={{background: 'linear-gradient(90deg, #006C69 0%, #B7BF72 100%)'}}></div>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-gray-900">
            Student Resource Hub
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            Access high-quality revision materials, exam strategies, and practice resources designed to help you succeed.
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 400, behavior: 'smooth' })}
            className="text-lg font-bold px-10 py-7 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #B7BF72 0%, #a8b565 100%)',
              color: '#000000'
            }}
          >
            Browse Resources
          </Button>
        </div>
      </div>

      {/* Top CTA Section - Find a Tutor */}
      <div className="bg-white border-b" style={{borderBottomColor: 'rgba(183, 191, 114, 0.15)'}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-gray-900">
            Not sure where to start?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Work with an expert tutor for personalized guidance and accelerated results
          </p>
          <Button
            onClick={() => navigate('/tutors')}
            className="font-semibold px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            style={{
              background: '#006C69',
              color: '#FFFFFF'
            }}
          >
            Find a Tutor →
          </Button>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">Explore by Subject</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose your subject area to access tailored revision materials, practice tests, and study guides.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {resourceCategories.map((category) => {
            const IconComponent = iconMap[category.icon];
            const ov = CATEGORY_OVERRIDES[category.id] || {};
            const ctaLabel = ov.cta || 'View Resources →';
            return (
              <Card
                key={category.id}
                className="p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer group border-2 hover:scale-[1.02] relative"
                style={{borderColor: 'rgba(183, 191, 114, 0.20)'}}
                onClick={() => handleCategoryClick(category.id)}
                data-testid={`resource-card-${category.id}`}
              >
                {ov.liveBadge && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-black text-[#B7BF72] border border-[#B7BF72]"
                    data-testid={`resource-badge-live-${category.id}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {ov.liveBadge}
                  </span>
                )}
                {ov.comingSoon && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200"
                    data-testid={`resource-badge-coming-${category.id}`}
                  >
                    Coming Soon
                  </span>
                )}
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                  style={{background: `${category.color}15`}}
                >
                  <IconComponent className="w-8 h-8" style={{color: category.color}} />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900 group-hover:text-[#006C69] transition-colors">
                  {category.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {category.description}
                </p>
                <Button
                  className="w-full font-semibold transition-all duration-300"
                  style={{
                    background: 'transparent',
                    border: `2px solid ${category.color}`,
                    color: category.color,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = category.color;
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = category.color;
                  }}
                  data-testid={`resource-cta-${category.id}`}
                >
                  {ctaLabel}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Premium Access Teaser */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Unlock Premium Resources
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get access to exclusive practice tests, detailed solutions, and expert strategies with a premium membership.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm" style={{color: '#B7BF72'}}>
            <span className="font-semibold">Coming Soon</span>
          </div>
        </div>
      </div>

      {/* Trust Signals */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{background: 'rgba(0, 108, 105, 0.15)'}}>
                <svg className="w-6 h-6" style={{color: '#006C69'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Exam-Ready Materials</h3>
              <p className="text-sm text-gray-600">Used by students preparing for top exams</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{background: 'rgba(183, 191, 114, 0.15)'}}>
                <svg className="w-6 h-6" style={{color: '#B7BF72'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Curriculum Aligned</h3>
              <p className="text-sm text-gray-600">Aligned with Australian curriculum standards</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{background: 'rgba(0, 108, 105, 0.15)'}}>
                <svg className="w-6 h-6" style={{color: '#006C69'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Expert Created</h3>
              <p className="text-sm text-gray-600">Developed by experienced tutors and educators</p>
            </div>
          </div>
        </div>
      </div>

      {/* Donation CTA */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div
          className="rounded-2xl bg-gradient-to-r from-black to-[#1a1a1a] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6"
          data-testid="resources-donate-cta"
        >
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs uppercase tracking-wide mb-3">
              Support · Education Access
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-2">
              Every dollar helps a child access educational resources.
            </h3>
            <p className="text-white/70 text-base">
              Free resources stay free when our community chips in. A small
              donation goes a long way.
            </p>
          </div>
          <Link
            to="/donate"
            data-testid="resources-donate-button"
            className="inline-flex items-center gap-2 bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-6 py-3 rounded-lg transition-colors whitespace-nowrap"
          >
            Donate to Support a Student
          </Link>
        </div>
      </div>

      <Footer />
      <FloatingActionButtons />
    </div>
  );
};

export default ResourcesHub;
